import { isValid, parse } from "date-fns";

import { type EligibilityTableRow, EMPTY_CELL_DISPLAY } from "@/billing/services/eligibility-table";
import { getCoverageStatusLabel } from "@/billing/utils/coverage-status-display";
import {
  createTableSorter,
  getNextTableSortState,
  SortDirection,
  TableSortState,
} from "@/billing/utils/table-sort";

export type EligibilitySortColumnKey =
  | "client"
  | "appointment"
  | "type"
  | "provider"
  | "paymentMethod"
  | "insurance"
  | "coverageEnd"
  | "lastVerified"
  | "coverageStatus";

export type EligibilitySortDirection = SortDirection;

export type EligibilityTableSortState = TableSortState<EligibilitySortColumnKey>;

/** Initial eligibility table sort: soonest upcoming appointment first. */
export const DEFAULT_ELIGIBILITY_TABLE_SORT_STATE: EligibilityTableSortState = {
  columnKey: "appointment",
  direction: "asc",
};

const APPOINTMENT_DISPLAY_FORMAT = "MM/dd/yyyy 'at' h:mm a";
const DATE_ONLY_DISPLAY_FORMAT = "MM/dd/yyyy";

/** Eligibility rows render missing data as "—", so that placeholder counts as an empty cell. */
function isEmptyCell(value: string | number | undefined): boolean {
  return value === undefined || value === "" || value === EMPTY_CELL_DISPLAY;
}

/**
 * Reads a displayed date back into a sortable timestamp.
 *
 * Returns the original string when it does not parse, so an unexpected value still orders
 * alphabetically among its peers instead of collapsing to the bottom with the blanks.
 */
function displayDateValue(value: string, formatString: string): string | number | undefined {
  if (isEmptyCell(value)) return undefined;

  const parsed = parse(value, formatString, new Date());
  return isValid(parsed) ? parsed.getTime() : value;
}

/**
 * Returns a new array of eligibility rows sorted by column and direction.
 * Empty cells sort after values with content.
 *
 * Client rows tie-break on MRN so two clients sharing a name keep a stable order.
 */
export const sortEligibilityTableRows = createTableSorter<
  EligibilityTableRow,
  EligibilitySortColumnKey
>(
  {
    client: [(row) => row.clientName, (row) => row.medicalRecordNumber],
    appointment: (row) => displayDateValue(row.upcomingAppointment, APPOINTMENT_DISPLAY_FORMAT),
    type: (row) => row.appointmentType,
    provider: (row) => row.provider,
    paymentMethod: (row) => row.paymentMethod,
    insurance: (row) => row.insurance,
    coverageEnd: (row) => displayDateValue(row.coverageEndDate, DATE_ONLY_DISPLAY_FORMAT),
    lastVerified: (row) => displayDateValue(row.lastVerified, DATE_ONLY_DISPLAY_FORMAT),
    coverageStatus: (row) => getCoverageStatusLabel(row.coverageStatus),
  },
  isEmptyCell,
);

/**
 * Returns the next sort state after a column header click.
 * The first click on a column sorts ascending; clicking it again reverses direction.
 */
export function getNextEligibilityTableSortState(
  current: EligibilityTableSortState | undefined,
  columnKey: EligibilitySortColumnKey,
): EligibilityTableSortState {
  return getNextTableSortState(current, columnKey);
}
